import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, StatCard, Section } from "../../components/Common";
import { PerformanceChart, AttendanceBars } from "../../components/Charts";
import { toast } from "sonner";
import {
  ArrowLeft, Trophy, CalendarCheck, Wallet, Users, Sparkles, FileText,
  Mail, Phone, BookOpen, UserPlus, X, Send,
} from "lucide-react";
import { formatCurrency } from "../../lib/currency";

const STATUS_COLORS = {
  present: "bg-emerald-50 text-emerald-700",
  absent: "bg-rose-50 text-rose-700",
  late: "bg-amber-50 text-amber-700",
  paid: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  overdue: "bg-rose-50 text-rose-700",
};

export default function AdminStudentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [genKind, setGenKind] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", phone: "" });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // {invite_link?, email_sent}

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/students/${id}/overview`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (kind) => {
    setGenKind(kind);
    try {
      const url = kind === "report_card" ? "/ai/report-card" : "/ai/insights";
      await api.post(url, { student_id: id });
      toast.success("Generated by Claude");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenKind(null);
    }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setInviteResult(null);
    try {
      const { data: res } = await api.post(`/admin/students/${id}/invite-parent`, inviteForm);
      setInviteResult(res);
      toast.success(res.email_sent ? "Invite email sent" : "Parent created — email mocked");
      setInviteForm({ email: "", name: "", phone: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  if (!data) {
    return <div className="text-center text-blue-600 font-heading animate-pulse py-16">Loading student…</div>;
  }

  const { student, parents, batches, attempts, attendance, attendance_summary, fees, fees_summary, ai, summary, homework } = data;

  return (
    <div data-testid="admin-student-detail">
      <Link to="/dashboard/admin/users" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to users
      </Link>

      <PageHeader
        title={student.name}
        subtitle={`${student.email} · ${batches.map((b) => b.name).join(", ") || "No batch assigned"}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <button onClick={() => generate("insight")} disabled={genKind === "insight"} data-testid="admin-gen-insight"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60">
              <Sparkles className="h-4 w-4" /> {genKind === "insight" ? "Thinking…" : "AI insight"}
            </button>
            <button onClick={() => generate("report_card")} disabled={genKind === "report_card"} data-testid="admin-gen-report"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60">
              <FileText className="h-4 w-4" /> {genKind === "report_card" ? "Writing…" : "Report card"}
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Avg score" value={`${summary.avg_percent}%`} sub={`${summary.tests_taken} tests`} icon={Trophy} accent="yellow" />
        <StatCard label="Attendance" value={`${attendance_summary.percent}%`} sub={`${attendance_summary.present}P / ${attendance_summary.absent}A / ${attendance_summary.late}L`} icon={CalendarCheck} accent="mint" />
        <StatCard label="Fees pending" value={formatCurrency(fees_summary.pending_amount)} sub={`${fees_summary.count_pending} open`} icon={Wallet} accent="coral" />
        <StatCard label="Parents linked" value={parents.length} icon={Users} />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Section title="Performance trend">
            <PerformanceChart attempts={attempts} />
          </Section>
        </div>
        <Section title="Attendance mix">
          <AttendanceBars counts={attendance_summary} />
        </Section>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <Section title={`Test history (${attempts.length})`}>
          {attempts.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">No tests submitted yet.</p>
          ) : (
            <div className="divide-y divide-slate-100 max-h-96 overflow-auto">
              {attempts.map((a) => (
                <div key={a.id} data-testid={`attempt-${a.id}`} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{a.test_title}</div>
                    <div className="text-xs text-slate-500">{new Date(a.submitted_at).toLocaleDateString()} · {a.score}/{a.total}</div>
                  </div>
                  <span className={`text-sm font-bold ${a.percent >= 70 ? "text-emerald-600" : a.percent >= 40 ? "text-amber-600" : "text-rose-600"}`}>{a.percent}%</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <Section title={`Homework record (${homework?.total || 0})`}>
          {!homework || homework.total === 0 ? (
            <p className="text-sm text-slate-500 py-4">No homework assigned yet.</p>
          ) : (
            <div data-testid="student-homework-record">
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">{homework.summary.on_time} on time</span>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">{homework.summary.late} late</span>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-rose-50 text-rose-700">{homework.summary.undone} undone</span>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{homework.summary.pending} pending</span>
              </div>
              <div className="divide-y divide-slate-100 max-h-96 overflow-auto">
                {homework.items.map((i) => (
                  <div key={i.homework_id} className="py-2.5">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-semibold text-slate-800">{i.title}</span>
                        {i.due_date && <span className="ml-2 text-xs text-slate-400">due {i.due_date}</span>}
                      </div>
                      <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        i.status === "on_time" ? "bg-emerald-50 text-emerald-700" :
                        i.status === "late" ? "bg-amber-50 text-amber-700" :
                        i.status === "undone" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"
                      }`}>{i.status.replace("_", " ")}{i.teacher_reviewed ? " ✓" : ""}</span>
                    </div>
                    {i.remarks && <p className="mt-1 text-xs text-slate-500 italic">"{i.remarks}"</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        <Section title="Attendance log">
          {attendance.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">No attendance recorded.</p>
          ) : (
            <div className="divide-y divide-slate-100 max-h-96 overflow-auto">
              {attendance.slice(0, 60).map((r, i) => (
                <div key={i} className="py-2.5 flex items-center justify-between">
                  <span className="text-sm text-slate-700">{r.date}</span>
                  <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <Section title={`Fees (${fees.length})`}>
          {fees.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">No fees on record.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {fees.map((f) => (
                <div key={f.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{formatCurrency(f.amount, f.currency)}</div>
                    <div className="text-xs text-slate-500">Due {f.due_date} · {f.description || "Tuition"}</div>
                  </div>
                  <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLORS[f.status]}`}>{f.status}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Parents & batches">
          <div className="space-y-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Linked parents</div>
              {parents.length === 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-400">No parent linked yet.</p>
                  <button onClick={() => setInviteOpen(true)} data-testid="open-invite-parent"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
                    <UserPlus className="h-4 w-4" /> Invite parent via email
                  </button>
                </div>
              ) : (
                parents.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50">
                    <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-bold text-sm">
                      {p.name[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 truncate">{p.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-3">
                        <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {p.email}</span>
                        {p.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {p.phone}</span>}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Batches</div>
              {batches.length === 0 ? (
                <p className="text-sm text-slate-400">Not enrolled in any batch.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {batches.map((b) => (
                    <span key={b.id} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-50 text-blue-700">
                      <BookOpen className="h-3 w-3" /> {b.name} · {b.subject}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Section>
      </div>

      <div className="mt-6">
        <Section title={`AI history (${ai.length})`}>
          {ai.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">No AI reports yet. Use "AI insight" or "Report card" above.</p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-auto">
              {ai.map((h) => (
                <div key={h.id} className="border-l-4 border-blue-500 bg-blue-50/30 rounded-r-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
                      {h.kind === "report_card" ? "Report card" : "Insights"}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">{new Date(h.created_at).toLocaleString()}</span>
                      {h.kind === "report_card" && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/ai/report-card/${h.id}/pdf?auth=${encodeURIComponent(localStorage.getItem("ym_access_token") || "")}`}
                          target="_blank" rel="noreferrer"
                          data-testid={`admin-download-pdf-${h.id}`}
                          className="text-xs font-semibold text-blue-600 hover:underline">
                          Download PDF →
                        </a>
                      )}
                    </div>
                  </div>
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{h.text}</pre>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {inviteOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 backdrop-blur-sm p-4" data-testid="invite-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-heading font-bold text-lg text-slate-900">Invite a parent</h3>
              <button onClick={() => { setInviteOpen(false); setInviteResult(null); }} className="p-1.5 rounded-lg hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-slate-600 mb-4">
                We'll create a parent account linked to <span className="font-semibold text-slate-900">{student.name}</span> and email a setup link so they can choose their own password.
              </p>
              <form onSubmit={submitInvite} className="space-y-3" data-testid="invite-form">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Parent's full name</label>
                  <input required value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    data-testid="invite-name"
                    className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Email</label>
                  <input required type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    data-testid="invite-email"
                    className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Phone (optional)</label>
                  <input value={inviteForm.phone} onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                    data-testid="invite-phone"
                    className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
                </div>

                {inviteResult?.invite_link && (
                  <div data-testid="invite-mock-link" className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">Email mocked</p>
                    <p className="text-xs text-amber-900 mb-2">Share this setup link with the parent directly:</p>
                    <a href={inviteResult.invite_link} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-700 underline break-all">{inviteResult.invite_link}</a>
                  </div>
                )}

                <button type="submit" disabled={inviting} data-testid="invite-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">
                  <Send className="h-4 w-4" /> {inviting ? "Sending…" : "Send invite"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
